import React, { useState, useEffect } from 'react';
import { useDataEngine } from '@dhis2/app-runtime';
import { Card, NoticeBox, CircularLoader, IconSync16, AlertBar, Input, Button } from '@dhis2/ui';
import { positionApi } from '../api/positionApi';
import { POSITION_ATTRIBUTES } from '../api/constants';
import { extractAttributes } from '../api/positionApi';

const StaffPositionEnforcement = () => {
  const engine = useDataEngine();
  const [unassignedStaff, setUnassignedStaff] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userOrgUnit, setUserOrgUnit] = useState();
  const [notification, setNotification] = useState({ visible: false, type: '', title: '', details: '' });
  const [searchTerm, setSearchTerm] = useState('');

  const showNotification = (type, title, details) => {
    setNotification({
      visible: true,
      type,
      title,
      details
    });
    setTimeout(() => {
      setNotification(prev => ({ ...prev, visible: false }));
    }, 5000);
  };

  // Get user's org unit
  useEffect(() => {
    const initializeUserData = async () => {
      try {
        const { me } = await engine.query({
          me: {
            resource: 'me',
            params: { fields: 'organisationUnits[id,name]' }
          }
        });
        if (me.organisationUnits && me.organisationUnits.length > 0) {
          setUserOrgUnit(me.organisationUnits[0].id);
        }
      } catch (error) {
        console.error('Failed to get user org unit:', error);
      }
    };
    initializeUserData();
  }, [engine]);

  // Enhanced position attribute extraction with better vacancy detection
  const getPositionDisplayInfo = (position) => {
    try {
      const attributes = extractAttributes(position.attributes);
      
      // Handle various attribute formats and fallbacks
      const positionId = attributes[POSITION_ATTRIBUTES.POSITION_ID] || 
                        position[POSITION_ATTRIBUTES.POSITION_ID] || 
                        'No ID';
      
      const positionTitle = attributes[POSITION_ATTRIBUTES.POSITION_TITLE] || 
                           position[POSITION_ATTRIBUTES.POSITION_TITLE] || 
                           'No Title';
      
      const currentIncumbent = attributes[POSITION_ATTRIBUTES.CURRENT_INCUMBENT] || 
                              position[POSITION_ATTRIBUTES.CURRENT_INCUMBENT];
      
      const status = attributes[POSITION_ATTRIBUTES.POSITION_STATUS] || 
                    position[POSITION_ATTRIBUTES.POSITION_STATUS] || 
                    'Active'; // Default to Active if not specified
      
      // Enhanced vacancy detection
      const isVacant = status === 'Vacant' || (!currentIncumbent && status === 'Active');
      
      return {
        id: positionId,
        title: positionTitle,
        currentIncumbent: currentIncumbent,
        status: status,
        displayText: `${positionId} - ${positionTitle} (${status})`,
        isVacant: isVacant
      };
    } catch (error) {
      console.error('Error extracting position info:', error, position);
      return {
        id: 'Error',
        title: 'Error extracting data',
        displayText: 'Error - Cannot read position data',
        isVacant: false
      };
    }
  };

  // Enhanced position fetching with better debugging
  const fetchPositions = async () => {
    if (!userOrgUnit) return [];

    try {
      console.log('🔄 Fetching positions for org unit:', userOrgUnit);
      
      const positionsResponse = await engine.query({
        positions: {
          resource: 'trackedEntityInstances',
          params: {
            program: positionApi.programId,
            trackedEntityType: 'UGGo0Ebjl0E',
            ou: userOrgUnit,
            fields: 'trackedEntityInstance,trackedEntityType,orgUnit,attributes[attribute,value]',
            pageSize: 1000,
            totalPages: true
          }
        }
      });

      const positionsData = positionsResponse.positions.trackedEntityInstances || [];
      console.log('📋 Raw positions data:', positionsData);
      
      // Log detailed info about each position
      positionsData.forEach((position, index) => {
        console.log(`Position ${index + 1} (TEI: ${position.trackedEntityInstance}):`, {
          attributes: position.attributes,
          orgUnit: position.orgUnit,
          displayInfo: getPositionDisplayInfo(position)
        });
      });

      return positionsData;
    } catch (error) {
      console.error('❌ Failed to fetch positions:', error);
      showNotification('error', 'Position Load Error', 'Failed to load position data');
      return [];
    }
  };

  // Fixed version - get staff from current org unit and its descendants only
  const findUnassignedStaff = async () => {
    if (!userOrgUnit) return;

    setLoading(true);
    try {
      console.log('👥 Finding unassigned staff...');

      // Get positions first
      const positionsData = await fetchPositions();
      setPositions(positionsData);

      // Get org unit hierarchy to find current org unit and its descendants
      const orgUnitTreeResponse = await engine.query({
        orgUnitTree: {
          resource: 'organisationUnits',
          params: {
            fields: 'id,name,level,children[id,name,level,children[id,name,level]]',
            filter: `id:eq:${userOrgUnit}`,
            paging: false
          }
        }
      });

      // Extract current org unit and all its descendants
      const orgUnitsInHierarchy = new Set();
      if (orgUnitTreeResponse.orgUnitTree.organisationUnits.length > 0) {
        const currentOrgUnit = orgUnitTreeResponse.orgUnitTree.organisationUnits[0];
        
        // Function to recursively add org units to the set
        const addOrgUnitAndDescendants = (orgUnit) => {
          orgUnitsInHierarchy.add(orgUnit.id);
          if (orgUnit.children && orgUnit.children.length > 0) {
            orgUnit.children.forEach(child => {
              addOrgUnitAndDescendants(child);
            });
          }
        };
        
        addOrgUnitAndDescendants(currentOrgUnit);
      }

      console.log('🏢 Org units in hierarchy:', Array.from(orgUnitsInHierarchy));

      // Get ALL staff first
      const usersResponse = await engine.query({
        users: {
          resource: 'users',
          params: {
            fields: 'id,firstName,surname,email,organisationUnits[id,name],userCredentials[username]',
            pageSize: 1000
          }
        }
      });

      const allUsers = usersResponse.users.users || [];
      console.log('📊 All staff in system:', allUsers.length);

      // Filter staff that belong to ANY org unit in our hierarchy
      const staffInHierarchy = allUsers.filter(user => 
        user.organisationUnits && user.organisationUnits.some(ou => orgUnitsInHierarchy.has(ou.id))
      );

      console.log('👥 Staff in current hierarchy:', staffInHierarchy.length);

      // Extract current incumbents from positions
      const assignedStaffIds = new Set();
      positionsData.forEach(position => {
        const info = getPositionDisplayInfo(position);
        if (info.currentIncumbent) {
          assignedStaffIds.add(info.currentIncumbent);
          console.log(`Position ${info.id} assigned to staff: ${info.currentIncumbent}`);
        }
      });

      console.log('✅ Assigned staff IDs:', Array.from(assignedStaffIds));

      // Find unassigned staff within our hierarchy
      const unassigned = staffInHierarchy.filter(staff => !assignedStaffIds.has(staff.id));
      setUnassignedStaff(unassigned);

      console.log(`✅ Found ${unassigned.length} unassigned staff out of ${staffInHierarchy.length} total staff in hierarchy`);

    } catch (error) {
      console.error('Error finding unassigned staff:', error);
      showNotification('error', 'Error', 'Failed to load staff data');
    } finally {
      setLoading(false);
    }
  };

  // Enhanced vacant positions filter
  const getVacantPositions = () => {
    const vacant = positions.filter(position => {
      const info = getPositionDisplayInfo(position);
      
      // Enhanced vacancy check - ensure status is explicitly "Vacant" AND no current incumbent
      const isVacant = (info.status === 'Vacant' || (!info.currentIncumbent && info.status === 'Active'));
      
      console.log('🔍 Vacancy Check:', {
        positionId: info.id,
        title: info.title,
        status: info.status,
        currentIncumbent: info.currentIncumbent,
        isVacant: isVacant
      });
      
      return isVacant;
    });
    
    console.log('🔄 Vacant positions found:', vacant.length);
    vacant.forEach(pos => {
      const info = getPositionDisplayInfo(pos);
      console.log('   -', info.id, info.title, `(Status: ${info.status})`);
    });
    
    return vacant;
  };

  // Filter staff based on search term
  const filteredStaff = unassignedStaff.filter(staff => {
    const fullName = `${staff.firstName || ''} ${staff.surname || ''}`.toLowerCase();
    const username = staff.userCredentials?.username?.toLowerCase() || '';
    const email = staff.email?.toLowerCase() || '';
    const searchLower = searchTerm.toLowerCase();
    
    return fullName.includes(searchLower) || 
           username.includes(searchLower) || 
           email.includes(searchLower);
  });

  useEffect(() => {
    if (userOrgUnit) {
      findUnassignedStaff();
    }
  }, [userOrgUnit]);

  const vacantPositions = getVacantPositions();

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularLoader />
        <span style={{ marginLeft: '10px' }}>Loading assignment data...</span>
      </div>
    );
  }

  return (
    <Card>
      <div style={{ padding: '1.5rem' }}>
        {/* Notification */}
        {notification.visible && (
          <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 1000, width: '350px' }}>
            <AlertBar
              {...(notification.type === 'success' ? { success: true } : 
                  notification.type === 'warning' ? { warning: true } : 
                  { error: true })}
              title={notification.title}
              onHidden={() => setNotification({ visible: false })}
            >
              {notification.details}
            </AlertBar>
          </div>      
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '1rem' }}>
          <h4 style={{ margin: 0 }}>Unassigned Staff Report</h4>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <Input
              placeholder="Search unassigned staff..."
              value={searchTerm}
              onChange={({ value }) => setSearchTerm(value)}
              style={{ minWidth: '200px' }}
            />
            <Button
              icon={<IconSync16 />}
              onClick={findUnassignedStaff}
              style={{ 
                boxShadow: "0 8px 10px rgba(108, 105, 105, 0.5)",
                color: 'white',
                backgroundColor: '#fd9a06ff',
                borderRadius: '8px',
                border: 'none',
                minWidth: '90px',
                height: '40px'
              }}
            >
              Refresh
            </Button>
          </div>
        </div>

        {unassignedStaff.length === 0 ? (
          <NoticeBox success title="All Staff Assigned">
            Great! All staff members have been assigned to positions.
          </NoticeBox>
        ) : (
          <>
            <NoticeBox warning title="Unassigned Staff Found">
              {unassignedStaff.length} staff member(s) need position assignments. 
              {vacantPositions.length > 0 ? ` ${vacantPositions.length} vacant position(s) available.` : ' No vacant positions available.'}
            </NoticeBox>

            {/* Staff List - View Only */}
            <div style={{ display: 'grid', gap: '1rem', marginTop: '1rem' }}>
              {filteredStaff.map(staff => (
                <div 
                  key={staff.id} 
                  style={{
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    padding: '1rem',
                    backgroundColor: '#f8f4eaff',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                  }}
                >
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: '1fr',
                    gap: '0.5rem'
                  }}>
                    {/* Staff Info */}
                    <div>
                      <div style={{ fontWeight: 'bold', fontSize: '1.1rem', marginBottom: '0.5rem' }}>
                        {`${staff.firstName || ''} ${staff.surname || ''}`.trim() || 'Unknown Name'}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.5rem', color: '#666' }}>
                        <div>
                          <strong>Username:</strong> {staff.userCredentials?.username || 'N/A'}
                        </div>
                        <div>
                          <strong>Email:</strong> {staff.email || 'N/A'}
                        </div>
                        <div>
                          <strong>Staff ID:</strong> {staff.id}
                        </div>
                        <div>
                          <strong>Org Units:</strong> {staff.organisationUnits?.map(ou => ou.name).join(', ') || 'N/A'}
                        </div>
                      </div>
                    </div>

                    {/* Vacant Positions Info (View Only) */}
                    <div style={{ 
                      marginTop: '0.5rem',
                      padding: '0.5rem',
                      backgroundColor: vacantPositions.length > 0 ? '#e8f5e8' : '#fff3cd',
                      borderRadius: '4px',
                      border: '1px solid #d1ecf1'
                    }}>
                      <div style={{ fontSize: '0.9rem', color: '#666' }}>
                        <strong>Available Positions:</strong> {vacantPositions.length}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Summary Statistics */}
            <div style={{ 
              marginTop: '2rem', 
              padding: '1rem',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #dee2e6'
            }}>
              <h5 style={{ margin: '0 0 1rem 0' }}>Summary</h5>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                <div>
                  <strong>Total Unassigned Staff:</strong> {unassignedStaff.length}
                </div>
                <div>
                  <strong>Vacant Positions:</strong> {vacantPositions.length}
                </div>
                <div>
                  <strong>Filtered Results:</strong> {filteredStaff.length}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </Card>
  );
};

export default StaffPositionEnforcement;